import uuid from 'uuid';
import Fuse from 'fuse.js';
import memoizeOne from 'memoize-one';
import qs from 'query-string';
import { MagicSearchPluginSync, MagicSearchPluginName, ResultItem } from './types';
import { SearchResultsListItemLink } from '../components';
import { FontAwesomeIconProps } from '@fortawesome/react-fontawesome';

const DEFAULT_ICON = ['far', 'folder'];

type ToolSearchIndexItem = {
  id?: string;
  icon?: FontAwesomeIconProps['icon'];
  title: string;
  description?: string | React.ReactNode;
  path: string;
};

const createToolSearchIndex = memoizeOne(
  (indexItems: ToolSearchIndexItem[]) =>
    new Fuse<ToolSearchIndexItem>(indexItems, {
      ignoreLocation: true,
      threshold: 0.0,
      includeScore: true,
      includeMatches: true,
      minMatchCharLength: 3,
      shouldSort: true,
      keys: [
        { name: 'title', weight: 1.4 },
        { name: 'description', weight: 0.5 },
      ],
      fieldNormWeight: 1.2,
    }),
);

const toolSearchPlugin = ({
  headerTitle,
  hint,
  indexItems,
}: {
  headerTitle: string;
  hint: string;
  indexItems: ToolSearchIndexItem[];
}): MagicSearchPluginSync => {
  const toolSearchIndex = createToolSearchIndex(indexItems);

  // lower scores are more relevant, and if there's no score, we just assume it's relevant
  const relevantScore = 0.3;

  return {
    name: MagicSearchPluginName.ToolSearchPlugin,
    hint: ({ searchScopes }) => (searchScopes.some(scope => scope.id === 'project') ? hint : null),
    onSearch: ({ searchText }) => {
      let toolSearchIndexResults = toolSearchIndex.search(searchText);
      toolSearchIndexResults = toolSearchIndexResults.slice(0, 10);
      toolSearchIndexResults = toolSearchIndexResults.filter(result => {
        return (result.score ?? 0) < relevantScore;
      });

      const results: ResultItem[] = [];

      if (!searchText) {
        return [];
      } else if (!toolSearchIndexResults.length) {
        return [];
      } else {
        results.push(
          ...toolSearchIndexResults.map(
            ({ item: { id, title, icon, description, path }, score }) => ({
              id: uuid.v4(),
              Component: SearchResultsListItemLink,
              props: {
                title,
                description,
                icon: icon ?? DEFAULT_ICON,
                to: {
                  pathname: path,
                  // We use the URL because we need something that also works in Anchors open in new tab or window.
                  ...(id
                    ? { search: qs.stringify({ focusElementId: encodeURIComponent(id) }) }
                    : {}),
                },
                type: headerTitle,
              },
              score,
            }),
          ),
        );
      }

      return results;
    },
  };
};

export { toolSearchPlugin };
